/**
 * The room's group chat from a browser: one socket to the live Worker
 * (`workers/live/chat.ts` is the other end), JSON both ways as
 * `shared/live-protocol.ts` defines it, and what the messages add up to. The
 * Watch page and the studio both hold the room's chat this way. Only what
 * `parseChatFromRoom` reads reaches the chat: a message the page does not know
 * is left unread.
 */
import {
  CHAT_MAX_CHARS,
  type ChatFromRoom,
  type ChatLine,
  type ChatRefusal,
  type ChatToRoom,
  LIVE_CLOSE,
  type LiveState,
  parseChatFromRoom,
  type PresenceFace,
  type ShareGuest,
  type ShareRule,
  type ShareStand,
} from "../../../shared/live-protocol";
import type { MomentRun } from "../../../shared/live-moments";
import { retryDelayMs } from "./retry";

/** What the chat shows, built from the room's messages. */
export interface RoomChat {
  lines: ChatLine[];
  pinned: ChatLine | null;
  /** How many are here, from the room's presence; null until it says. */
  here: number | null;
  /** The live's state as the room last said it. */
  state: LiveState | null;
  /** Why the last message was not taken, until the next one is. */
  refused: ChatRefusal | null;
  /** The newest people here, by name and photo, as the room's presence says them. */
  faces: PresenceFace[];
  /** No host socket is open and the room is waiting for the host to come back. */
  hostAway: boolean;
  /** Which of those faces is this page's own person, once the room has said; null for the host. */
  you: string | null;
  /** Who shares a screen: the host's rule, a guest's own stand, and the host's list of asks. */
  share: { rule: ShareRule; mine: ShareStand; asks: ShareGuest[]; allowed: ShareGuest | null };
  /** What the host has done with this session's planned moments; null until the room says. */
  runs: MomentRun[] | null;
}

export const EMPTY_CHAT: RoomChat = {
  lines: [],
  pinned: null,
  here: null,
  state: null,
  refused: null,
  faces: [],
  hostAway: false,
  you: null,
  share: { rule: "ask", mine: "none", asks: [], allowed: null },
  runs: null,
};

/** The most lines a page keeps; the room sends a new socket its last 50. */
const KEEP = 300;

/**
 * What the chat hears: the room's messages, its own socket opening ("joined":
 * the room sends the recent messages again, so they replace what was held),
 * and a message of its own going out ("sent").
 */
export type ChatEvent = ChatFromRoom | { type: "joined" } | { type: "sent" };

export function chatReducer(chat: RoomChat, event: ChatEvent): RoomChat {
  switch (event.type) {
    case "joined":
      return { ...chat, lines: [], pinned: null };
    case "sent":
      return chat.refused ? { ...chat, refused: null } : chat;
    case "said": {
      if (chat.lines.some((l) => l.id === event.message.id)) return chat;
      const lines = [...chat.lines, event.message];
      return { ...chat, lines: lines.length > KEEP ? lines.slice(-KEEP) : lines };
    }
    case "hidden": {
      const lines = chat.lines.filter((l) => l.id !== event.id);
      const pinned = chat.pinned?.id === event.id ? null : chat.pinned;
      return { ...chat, lines, pinned };
    }
    case "pinned":
      return { ...chat, pinned: event.message };
    case "presence":
      return { ...chat, here: event.here, faces: event.faces ?? chat.faces, you: event.you ?? chat.you, hostAway: event.hostAway === true };
    case "share":
      return { ...chat, share: { ...chat.share, rule: event.rule, mine: event.mine } };
    case "share-asks":
      return { ...chat, share: { ...chat.share, rule: event.rule, asks: event.asks, allowed: event.allowed } };
    case "moments":
      return { ...chat, runs: event.runs };
    case "state":
      return { ...chat, state: event.state };
    case "refused":
      return { ...chat, refused: event.reason };
    // Reactions float over the picture; they are not part of what the chat shows.
    case "reacted":
      return chat;
  }
}

/** A refusal in plain words. */
export function refusalWords(reason: ChatRefusal): string {
  switch (reason) {
    case "too-fast":
      return "That was too fast. Wait a second and send it again.";
    case "too-long":
      return `That is too long. Keep it under ${CHAT_MAX_CHARS} characters.`;
    case "closed":
      return "The room chat is closed.";
    case "empty":
      return "Write something first.";
    case "host-only":
      return "Only the host can do that.";
  }
}

export interface RoomChatSocket {
  /** Send to the room (say, react, share, and the host's pin, hide and answers); false when the socket is not open. */
  send: (msg: ChatToRoom) => boolean;
  close: () => void;
  /** Say goodbye as the page goes away, but keep the socket's rejoining: a page kept for later comes back whole. */
  sayGoodbye: (goodbye: { code: number; reason: string }) => void;
}

/**
 * Hold the chat socket open, for the Watch page and the studio alike: a
 * dropped socket joins again with a fresh address, waiting longer after each
 * failed try, and a close that says the live has ended stays closed.
 */
export function openRoomChat(
  url: () => Promise<string>,
  hear: (event: ChatEvent) => void,
  ended: () => void = () => {},
): RoomChatSocket {
  let ws: WebSocket | null = null;
  let stopped = false;
  let attempt = 0;
  let retry: number | undefined;
  const later = () => {
    retry = window.setTimeout(() => void join(), retryDelayMs(attempt++));
  };
  const join = async () => {
    let address: string;
    try {
      address = await url();
    } catch {
      if (!stopped) later();
      return;
    }
    if (stopped) return;
    const next = new WebSocket(address);
    ws = next;
    next.addEventListener("open", () => {
      attempt = 0;
      hear({ type: "joined" });
    });
    next.addEventListener("message", (e: MessageEvent) => {
      if (typeof e.data !== "string") return;
      const msg = parseChatFromRoom(e.data);
      if (msg) hear(msg);
    });
    next.addEventListener("close", (e: CloseEvent) => {
      if (ws === next) ws = null;
      if (stopped) return;
      if (e.code === LIVE_CLOSE.ended.code) {
        ended();
        return;
      }
      later();
    });
  };
  void join();
  return {
    send: (msg) => {
      if (!ws || ws.readyState !== WebSocket.OPEN) return false;
      ws.send(JSON.stringify(msg));
      if (msg.type === "say") hear({ type: "sent" });
      return true;
    },
    close: () => {
      stopped = true;
      window.clearTimeout(retry);
      ws?.close();
      ws = null;
    },
    sayGoodbye: (goodbye) => ws?.close(goodbye.code, goodbye.reason),
  };
}
